/**
 * useAppState.js — Hook centralisant l'état et les handlers de App.jsx
 *
 * v10.5.103 : Découpage modulaire
 *   - useAuthState  → restauration session, login/logout, 2FA
 *   - useAppState   → données métier, navigation, CRUD (ce fichier)
 */
import { PAGES }         from '../constants/pages';
import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders, authFetch } from '../utils/authFetch';
import { useAuthState }      from './useAuthState';

export default function useAppState() {
  // ─── Auth (délégué) ────────────────────────────────────────────────────
  const {
    authPage, setAuthPage,
    currentUser,
    verificationMessage,
    pending2FA, setPending2FA,
    handle2FASuccess,
    handleLogin: _handleLogin,
    handleLogout: _handleLogout,
  } = useAuthState({
    onAuthenticated: () => { loadHorses(); loadSessions(); },
  });

  // ─── Dark mode ─────────────────────────────────────────────────────────
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ekynair-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  useEffect(() => { document.body.classList.toggle('dark-mode', darkMode); }, [darkMode]);

  // ─── Navigation ────────────────────────────────────────────────────────
  // Atterrissage Sessions systématique (login ET refresh/"/" nu). L'app est
  // une SPA mono-URL : la racine "/" est la seule URL, donc "refresh sur /" =
  // remount avec currentPage initial. On abandonne la mémoire d'onglet
  // localStorage (sprint 2026-05-29, supersede l'ancienne persistence) : le
  // shell propriétaire n'a AUCUNE route profonde persistée (selectedSession
  // est un state React éphémère), donc rien à respecter au refresh côté owner.
  // Symétrique au mode véto, où la persistance profonde vit dans useVetProState.
  const [currentPage, setCurrentPage] = useState(PAGES.LIST);

  // ─── Données métier ────────────────────────────────────────────────────
  const [horses,          setHorses]          = useState([]);
  const [sessions,        setSessions]        = useState([]);
  const [sharedHorses,    setSharedHorses]    = useState([]);
  const [selectedHorse,   setSelectedHorse]   = useState(null);
  const [selectedSession,   setSelectedSession]   = useState(null);
  const [compareSessionIds, setCompareSessionIds] = useState([]);
  const [editingHorse,    setEditingHorse]    = useState(null);
  const [healthHorse,     setHealthHorse]     = useState(null);
  const [detailHorse,     setDetailHorse]     = useState(null);
  const [liveHorse,       setLiveHorse]       = useState(null);
  // shareRoles : Map horseId → { role, acceptedAt } — isolé par cheval (ARCH-4)
  // acceptedAt borne les droits d'écriture d'une personne de confiance (v10.5.126)
  const [shareRoles,   setShareRoles]   = useState({});  // { [horseId]: { role, acceptedAt } }
  const [activeHorseForShare, setActiveHorseForShare] = useState(null);

  const setShareRoleForHorse = (horseId, role, acceptedAt = null) => {
    if (horseId) {
      const entry = role ? { role, acceptedAt: acceptedAt || null } : null;
      setShareRoles(prev => ({ ...prev, [horseId]: entry }));
    }
    setActiveHorseForShare(horseId || null);
  };

  // Getter — retourne { role, acceptedAt } pour un cheval donné, ou null
  const getShareRole = (horseId) => {
    const id = horseId || activeHorseForShare;
    return id ? (shareRoles[id] || null) : null;
  };
  const [sensorType,      setSensorType]      = useState('halter');

  // ─── Modales + upload ──────────────────────────────────────────────────
  const [confirmModal,      setConfirmModal]      = useState({ isOpen: false, sessionId: null });
  const [confirmHorseModal, setConfirmHorseModal] = useState({ isOpen: false, horseId: null, horseName: '' });
  const [uploading,         setUploading]         = useState(false);
  const [uploadError,       setUploadError]       = useState(null);

  // ─── Data loaders ───────────────────────────────────────────────────────
  // Toutes les deps sont des setters React (stables) ou des imports (stables)
  // → useCallback([]) est safe, pas de stale closure
  const doLogout = useCallback(() => _handleLogout({ setCurrentPage, setHorses, setSessions }), [_handleLogout]);

  const loadHorses = useCallback(async () => {
    try {
      const res = await fetch('/api/horses', { headers: getAuthHeaders(), credentials: 'include' });
      if (res.status === 401) { doLogout(); return; }
      const data = await res.json();
      if (data.success) setHorses(data.horses);
    } catch (e) { console.error('loadHorses:', e); }
    try {
      const res2  = await fetch('/api/shared/horses', { headers: getAuthHeaders(), credentials: 'include' });
      const data2 = await res2.json();
      if (data2.success) setSharedHorses(data2.horses || []);
    } catch { /* silent */ }
  }, [doLogout]);

  const loadSessions = useCallback(async (horseId = null) => {
    try {
      const url = horseId ? `/api/sessions?horse_id=${horseId}` : '/api/sessions';
      const res = await fetch(url, { headers: getAuthHeaders(), credentials: 'include' });
      if (res.status === 401) { doLogout(); return; }
      const data = await res.json();
      if (data.success) setSessions(data.sessions);
    } catch (e) { console.error('loadSessions:', e); }
  }, [doLogout]);

  // ─── Auth — chargement initial quand app prête ──────────────────────────
  useEffect(() => {
    if (authPage === 'app' && currentUser) {
      loadHorses();
      loadSessions();
    }
  }, [authPage, currentUser, loadHorses, loadSessions]);

  // Wrappers pour injecter les setters locaux dans useAuthState
  const handleLogin = (user) => _handleLogin(user, { setCurrentPage, loadSessions });
  const handleLogout = () => doLogout();

  // ─── Session CRUD ───────────────────────────────────────────────────────
  const handleDeleteSession = (sessionId, e) => {
    e.stopPropagation();
    setConfirmModal({ isOpen: true, sessionId });
  };

  const confirmDeleteSession = async () => {
    const { sessionId } = confirmModal;
    setConfirmModal({ isOpen: false, sessionId: null });
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' });
      const data = await res.json();
      if (data.success) loadSessions(selectedHorse);
    } catch (e) { console.error('deleteSession:', e); }
  };

  const handleDeleteSessions = async (sessionIds) => {
    try {
      await Promise.all(sessionIds.map(id =>
        fetch(`/api/sessions/${id}`, { method: 'DELETE', credentials: 'include', headers: getAuthHeaders() })
      ));
      loadSessions(selectedHorse);
    } catch (e) { console.error('deleteSessions:', e); }
  };

  // ─── Horse CRUD ─────────────────────────────────────────────────────────
  const handleEditHorse = async (horseIdOrObject) => {
    const horseId = horseIdOrObject?._id || horseIdOrObject;
    if (!horseId) return;
    const local = horses.find(h => h._id === horseId);
    if (local) { setEditingHorse(local); return; }
    try {
      const res  = await fetch(`/api/horses/${horseId}`, { credentials: 'include', headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success && data.horse) setEditingHorse(data.horse);
    } catch { /* silent */ }
  };

  const handleHorseUpdated = (updatedHorse) => {
    setHorses(horses.map(h => h._id === updatedHorse._id ? updatedHorse : h));
    setEditingHorse(null);
  };

  const handleDeleteHorse = (horseId) => {
    const horse = horses.find(h => h._id === horseId);
    setConfirmHorseModal({ isOpen: true, horseId, horseName: horse?.horseName || 'ce cheval' });
  };

  const confirmDeleteHorse = async () => {
    const { horseId } = confirmHorseModal;
    setConfirmHorseModal({ isOpen: false, horseId: null, horseName: '' });
    try {
      const res  = await fetch(`/api/horses/${horseId}`, { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' });
      const data = await res.json();
      if (data.success) loadHorses();
    } catch (e) { console.error('deleteHorse:', e); }
  };

  // ─── CSV Upload ─────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    e.preventDefault();
    setUploadError(null);
    setUploading(true);
    try {
      const res = await authFetch('/api/sessions/upload', { method: 'POST', body: new FormData(e.target) });
      if (!res) return;
      let data;
      try { data = await res.json(); } catch {
        setUploadError(`Erreur serveur (HTTP ${res.status}) — réessayez ou contactez le support`);
        return;
      }
      if (data.success) {
        e.target.reset();
        // V1.2-Y (17/5/2026) — backend sessionUploadController.js:183-186
        // retourne TOUJOURS `session.id` en succès. La branche else
        // historique (loadSessions + setCurrentPage(PAGES.LIST)) était
        // inatteignable et a été supprimée (cf. BACKLOG V1.2-Y, D71).
        setSelectedSession(data.session.id || data.session._id);
      } else {
        setUploadError(data.error || data.message || "Erreur lors de l'import");
      }
    } catch { setUploadError('Erreur de connexion — vérifiez votre réseau et réessayez'); }
    finally  { setUploading(false); }
  };

  // ─── Navigation ─────────────────────────────────────────────────────────
  const handleNavigate = (page) => {
    setSelectedSession(null);
    if (page === PAGES.LIST)   { setSelectedHorse(null); loadSessions(null); }
    if (page === PAGES.HORSES) { loadHorses(); }
    setCurrentPage(page);
  };

  const openHorsePage = async (horseId, page, setter) => {
    let horse = horses.find(h => h._id === horseId);
    if (!horse) {
      try {
        const res  = await fetch(`/api/horses/${horseId}`, { credentials: 'include', headers: getAuthHeaders() });
        const data = await res.json();
        if (data.success) horse = data.horse;
      } catch { /* silent */ }
    }
    if (horse) { setter(horse); setCurrentPage(page); }
  };

  return {
    // Auth
    authPage, setAuthPage, currentUser, verificationMessage,
    pending2FA, setPending2FA, handle2FASuccess, handleLogin, handleLogout,
    // UI
    darkMode, setDarkMode, currentPage, setCurrentPage,
    // Data
    horses, sessions, sharedHorses,
    selectedHorse, setSelectedHorse, selectedSession, setSelectedSession,
    editingHorse, setEditingHorse, healthHorse, setHealthHorse,
    detailHorse, setDetailHorse, setShareRoleForHorse, getShareRole, shareRoles,
    compareSessionIds, setCompareSessionIds,
    liveHorse, setLiveHorse, sensorType, setSensorType,
    // Modales
    confirmModal, setConfirmModal, confirmHorseModal, setConfirmHorseModal,
    uploading, uploadError, setUploadError,
    // Handlers
    loadHorses, loadSessions,
    handleDeleteSession, confirmDeleteSession, handleDeleteSessions,
    handleEditHorse, handleHorseUpdated, handleDeleteHorse, confirmDeleteHorse,
    handleFileUpload, handleNavigate, openHorsePage,
  };
}
